import React, { useEffect, useRef, useState } from 'react';
import { useEffectsStore } from '@/store/effects.store.ts';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
  originalX: number;
  originalY: number;
  baseSpeed: number;
  angle: number;
  energy: number;
}

interface RippleEffect {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
  startTime: number;
}

interface ParticleBackgroundProps {
  particleCount?: number;
  colors?: string[];
  className?: string;
}

const ParticleBackground: React.FC<ParticleBackgroundProps> = ({
  particleCount = 80,
  colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b'],
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const ripplesRef = useRef<RippleEffect[]>([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  const { 
    particlesEnabled, 
    clickEffectsEnabled, 
    mouseAttractionEnabled 
  } = useEffectsStore();

  const createParticle = (width: number, height: number): Particle => {
    const x = Math.random() * width;
    const y = Math.random() * height;
    const angle = Math.random() * Math.PI * 2;
    const baseSpeed = Math.random() * 1.5 + 1.0;
    
    return {
      x,
      y,
      vx: Math.cos(angle) * baseSpeed,
      vy: Math.sin(angle) * baseSpeed,
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.6 + 0.2,
      color: colors[Math.floor(Math.random() * colors.length)],
      originalX: x,
      originalY: y,
      baseSpeed,
      angle,
      energy: 1.0
    };
  };

  const initParticles = (width: number, height: number) => {
    particlesRef.current = Array.from({ length: particleCount }, () =>
      createParticle(width, height)
    );
  };

  const updateParticles = (width: number, height: number) => {
    if (!particlesEnabled) return;

    particlesRef.current.forEach((particle) => {
      particle.x += particle.vx;
      particle.y += particle.vy;

      if (mouseAttractionEnabled && particlesEnabled) {
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const captureRadius = 150;
        const orbitRadius = 40;
        const elasticZone = 30;
        
        if (distance < captureRadius) {
          if (distance > orbitRadius + elasticZone) {
            const attractionForce = ((captureRadius - distance) / captureRadius) * 0.05;
            particle.vx += (dx / distance) * attractionForce;
            particle.vy += (dy / distance) * attractionForce;
          } else if (distance < orbitRadius - elasticZone) {
            const repelForce = ((orbitRadius - elasticZone - distance) / (orbitRadius - elasticZone)) * 0.02;
            particle.vx -= (dx / distance) * repelForce;
            particle.vy -= (dy / distance) * repelForce;
          } else {
            const tangentX = -dy / distance;
            const tangentY = dx / distance;
            const orbitForce = 0.008;
            particle.vx += tangentX * orbitForce;
            particle.vy += tangentY * orbitForce;
            const centripetalForce = 0.003;
            particle.vx += (dx / distance) * centripetalForce;
            particle.vy += (dy / distance) * centripetalForce;
          }
        }
      }

      if (particle.x < -particle.size) particle.x = width + particle.size;
      else if (particle.x > width + particle.size) particle.x = -particle.size;
      if (particle.y < -particle.size) particle.y = height + particle.size;
      else if (particle.y > height + particle.size) particle.y = -particle.size;

      const currentSpeed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
      if (currentSpeed < particle.baseSpeed * 0.2) {
        const energyBoost = particle.baseSpeed * 0.15;
        particle.vx += Math.cos(particle.angle) * energyBoost;
        particle.vy += Math.sin(particle.angle) * energyBoost;
        particle.angle += (Math.random() - 0.5) * 0.2;
      }

      const maxSpeed = particle.baseSpeed * 2.0;
      if (currentSpeed > maxSpeed) {
        particle.vx = (particle.vx / currentSpeed) * maxSpeed;
        particle.vy = (particle.vy / currentSpeed) * maxSpeed;
      }

      particle.vx *= 0.995;
      particle.vy *= 0.995;
    });
  };

  const updateRipples = () => {
    if (!clickEffectsEnabled) return;
    const currentTime = Date.now();
    ripplesRef.current = ripplesRef.current.filter(ripple => {
      const elapsed = currentTime - ripple.startTime;
      const progress = elapsed / 1500;
      if (progress >= 1) return false;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      ripple.radius = ripple.maxRadius * easeOut;
      ripple.opacity = (1 - progress) * 0.8;
      return true;
    });
  };

  const drawParticles = (ctx: CanvasRenderingContext2D) => {
    if (!particlesEnabled) return;
    const particles = particlesRef.current;

    particles.forEach((particle, i) => {
      particles.slice(i + 1).forEach((otherParticle) => {
        const dx = particle.x - otherParticle.x;
        const dy = particle.y - otherParticle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 80) {
          const opacity = (80 - distance) / 80 * 0.3;
          ctx.beginPath();
          ctx.strokeStyle = `rgba(59, 130, 246, ${opacity})`;
          ctx.lineWidth = 0.8;
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(otherParticle.x, otherParticle.y);
          ctx.stroke();
        }
      });
      
      if (mouseAttractionEnabled && particlesEnabled) {
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 150) {
          const opacity = (150 - distance) / 150 * 0.5;
          let lineColor = `rgba(59, 130, 246, ${opacity})`;
          if (distance < 40) lineColor = `rgba(59, 130, 246, ${opacity})`;
          else if (distance < 70) lineColor = `rgba(139, 92, 246, ${opacity})`;
          else lineColor = `rgba(255, 107, 107, ${opacity})`;
          ctx.beginPath();
          ctx.strokeStyle = lineColor;
          ctx.lineWidth = 1.5;
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
          ctx.stroke();
        }
      }
    });

    particles.forEach((particle) => {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      const gradient = ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, particle.size * 2
      );
      gradient.addColorStop(0, particle.color + Math.floor(particle.opacity * 255).toString(16).padStart(2, '0'));
      gradient.addColorStop(1, particle.color + '00');
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.shadowBlur = 8;
      ctx.shadowColor = particle.color;
      ctx.fill();
      ctx.shadowBlur = 0;
    });
  };

  const drawRipples = (ctx: CanvasRenderingContext2D) => {
    if (!clickEffectsEnabled) return;
    ripplesRef.current.forEach(ripple => {
      ctx.beginPath();
      ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(59, 130, 246, ${ripple.opacity * 0.6})`;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(ripple.x, ripple.y, ripple.radius * 0.7, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(139, 92, 246, ${ripple.opacity * 0.4})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(ripple.x, ripple.y, ripple.radius * 0.3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(16, 185, 129, ${ripple.opacity * 0.8})`;
      ctx.fill();
    });
  };

  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    updateParticles(canvas.width, canvas.height);
    updateRipples();
    drawParticles(ctx);
    drawRipples(ctx);
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const handleMouseMove = (event: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    mouseRef.current.x = event.clientX - rect.left;
    mouseRef.current.y = event.clientY - rect.top;
  };

  const handleClick = (event: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas || !clickEffectsEnabled) return;
    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    ripplesRef.current.push({
      x: clickX,
      y: clickY,
      radius: 0,
      maxRadius: Math.random() * 80 + 60,
      opacity: 1,
      startTime: Date.now()
    });
    particlesRef.current.forEach(particle => {
      const dx = particle.x - clickX;
      const dy = particle.y - clickY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 150) {
        const force = (150 - distance) / 150;
        particle.vx += (dx / distance) * force * 8;
        particle.vy += (dy / distance) * force * 8;
      }
    });
  };

  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { innerWidth, innerHeight } = window;
    canvas.width = innerWidth;
    canvas.height = innerHeight;
    setDimensions({ width: innerWidth, height: innerHeight });
    initParticles(innerWidth, innerHeight);
  };

  useEffect(() => {
    handleResize();
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animate();
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (!particlesEnabled && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    } else if (particlesEnabled && !animationFrameRef.current) {
      animate();
    }
  }, [particlesEnabled, clickEffectsEnabled, mouseAttractionEnabled]);

  if (!particlesEnabled) return null;

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none z-50 ${className}`}
      style={{ background: 'transparent' }}
    />
  );
};

export default ParticleBackground;
